import React, { useState, useEffect, Fragment } from 'react';
import { Breadcrumbs, H5 } from '../../../../AbstractElements';
import { Card, CardBody, CardHeader, Col, Container, Row, Input, Media, FormGroup } from 'reactstrap';
import DataTable from 'react-data-table-component';
import { PlusCircle, Edit } from 'react-feather';
import { fetchTableData } from '../../../../Api/ApiMethods';
import { useNavigate } from 'react-router-dom';

const Customers = () => {
    const [data, setData] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [search] = useState('');
    const [totalRows, setTotalRows] = useState(0);
    const [customerTypeFilter, setCustomerTypeFilter] = useState('Bireysel');
    const [filterText, setFilterText] = useState('');

    const customerApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/customer';
    const navigate = useNavigate();

    const handleSearch = (event) => {
        setFilterText(event.target.value);
    };

    useEffect(() => {
        const apiUrl = `${customerApiUrl}?customerType=${customerTypeFilter}`;
        
        fetchTableData({
            apiEndpoint: apiUrl,
            page,
            pageSize,
            search,
            setDataCallback: (fetchedData) => {
                setData(fetchedData);
            },
            setTotalRowsCallback: setTotalRows,
        });
    }, [page, pageSize, search, customerTypeFilter]);

    const handleDetail = (id) => {
        navigate(`/customer/${id}`);
    };

    const handleNew = () => {
        navigate('/customer/new', { state: { isNew: true } });
    };

    // Handle customer type filter change
    const handleCustomerTypeChange = (e) => {
        // Reset pagination first, then update filter
        // This ensures we don't try to fetch non-existent pages
        setPage(1);
        setPageSize(10);
        setData([]); // Clear existing data to prevent showing incorrect data during loading
        setTotalRows(0); // Reset total rows count
        setCustomerTypeFilter(e.target.value);
    };

    const columns = customerTypeFilter === 'Bireysel' ? [
        { name: 'Müşteri Tipi', selector: row => row.customerType || '', sortable: true },
        { name: 'Vekaleti Alan', selector: row => row.agent ? `${row.agent.name || ''} ${row.agent.surname || ''}` : '', sortable: true },
        { name: 'Müşteri Adı', selector: row => row.clientName || '', sortable: true },
        { name: 'Kimlik Numarası', selector: row => row.identityNumber || '', sortable: true },
        { name: 'E-posta', selector: row => (row.emails && row.emails[0]?.email) || '', sortable: true },
        { name: 'Telefon', selector: row => (row.phones && row.phones[0]?.phone) || '', sortable: true },
        {
            name: 'Düzenle',
            cell: row => (
                <div className="btn btn-outline-warning d-flex align-items-center justify-content-center" onClick={() => handleDetail(row.id)}>
                    <Edit size={18} />
                </div>
            ),
            ignoreRowClick: true,
            right: true,
            button: true,
        },
    ] : [
        { name: 'Müşteri Tipi', selector: row => row.customerType || '', sortable: true },
        { name: 'Vekaleti Alan', selector: row => row.agent ? `${row.agent.name || ''} ${row.agent.surname || ''}` : '', sortable: true },
        { name: 'Şirket Adı', selector: row => row.companyName || '', sortable: true },
        { name: 'Vergi Numarası', selector: row => row.taxNumber || '', sortable: true },
        { name: 'CRS Numarası', selector: row => row.crsNumber || '', sortable: true },
        { name: 'Şirket Temsilcisi', selector: row => row.companyRepresentative || '', sortable: true },
        { name: 'E-posta', selector: row => (row.emails && row.emails[0]?.email) || '', sortable: true },
        { name: 'Telefon', selector: row => (row.phones && row.phones[0]?.phone) || '', sortable: true },
        {
            name: 'Düzenle',
            cell: row => (
                <div className="btn btn-outline-warning d-flex align-items-center justify-content-center" onClick={() => handleDetail(row.id)}>
                    <Edit size={18}/>
                </div>
            ),
            ignoreRowClick: true,
            right: true,
            button: true,
        },
    ];

    const filteredItems = data.filter(
        item => {
            if (!filterText) return true;
            
            const searchableValues = [
                item.customerType,
                item.clientName,
                item.identityNumber,
                item.companyName,
                item.taxNumber,
                item.crsNumber,
                item.companyRepresentative,
                item.emails?.[0]?.email,
                item.phones?.[0]?.phone,
                item.agent ? `${item.agent.name} ${item.agent.surname}` : ''
            ].filter(Boolean).join(' ').toLowerCase();
            
            return searchableValues.includes(filterText.toLowerCase());
        }
    );

    return (
        <>
            <Breadcrumbs parent='Müşteri Alanları' title='Müşteriler' />
            <Container fluid={true}>
                <Row>
                    <Col sm='12'>
                        <Card>
                            <CardHeader>
                                <H5>Müşteriler</H5>
                                <span>Mevcut olan müşteriler aşağıda listelenmektedir.</span>
                            </CardHeader>
                            <CardBody>
                                <div className="d-flex justify-content-end align-items-center w-100 file-content flex-wrap">
                                    <FormGroup className="d-flex align-items-center me-2 mt-3 flex-wrap">
                                        <Input
                                            type="select"
                                            id="customerType"
                                            name="customerType"
                                            value={customerTypeFilter}
                                            onChange={handleCustomerTypeChange}
                                            className="form-control"
                                            style={{ height: '35px' }}
                                        >
                                            <option value="Bireysel">Bireysel</option>
                                            <option value="Kurumsal">Kurumsal</option>
                                        </Input>
                                    </FormGroup>
                                    <div className='form-inline me-2 p-0 custom-responsive-width'>
                                        <div className='mb-0 form-group d-flex align-items-center border rounded px-2' style={{ height: '35px' }}>
                                            <i className='fa fa-search text-muted'></i>
                                            <Input
                                                type="text"
                                                placeholder="Ara.."
                                                value={filterText}
                                                onChange={handleSearch}
                                                className="border-0 ms-2 shadow-none"
                                                style={{ height: '33px', flexGrow: 1 }}
                                            />
                                        </div>
                                    </div>
                                    <Media body className='text-end'>
                                        <div className="btn btn-outline-primary ms-2" onClick={handleNew} style={{ height: '35px', display: 'inline-flex', alignItems: 'center' }}>
                                            <PlusCircle size={18} className="me-1" />
                                            {' Müşteri Ekle'}
                                        </div>
                                    </Media>
                                </div>

                                <div style={{ height: "40px" }}></div>

                                <Fragment>
                                    <DataTable
                                        columns={columns}
                                        data={filteredItems}
                                        pagination
                                        paginationServer
                                        selectableRows={false}
                                        paginationTotalRows={totalRows}
                                        onChangePage={page => setPage(page)}
                                        onChangeRowsPerPage={pageSize => setPageSize(pageSize)}
                                    />
                                </Fragment>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </>
    );
};

export default Customers;
